import { isCurvance } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Curvance — the only lender we carry whose position **cannot be closed on
 * demand**, and the reason `BorrowExitTerms.cooldownSecs` exists.
 *
 * Two hard gates the generic builder cannot see, both of which reverted for a
 * real user before this adapter existed:
 *
 *  1. **`MIN_HOLD_PERIOD` — 20 minutes.** Stamped per account per
 *     MarketManager by POSTING COLLATERAL and by BORROWING; while it runs the
 *     protocol reverts repayment, redemption AND share transfers. So a
 *     position cannot be closed, deleveraged or migrated out for 20 minutes
 *     after it is opened or topped up. Curvance acknowledged and declined to
 *     fix the consequence: an incoming liquidation cannot be averted by
 *     repaying during the window (topping up collateral still works).
 *
 *     The generic builder hardcodes `availability.canClose: true` because
 *     "freezes stop entry, not exit" holds on every other lender. Here it does
 *     not, so the sheet has to say so.
 *
 *  2. **`MIN_LOAN_SIZE` — $10, and it gates the EXIT too.** Opening below it
 *     reverts, and so does a partial repay that would leave residual debt
 *     below it; a full repay is always allowed. Unusually the floor is
 *     denominated in **USD (WAD)**, not in the debt asset's own units — every
 *     other lender's floor is in base units — so it must be converted at the
 *     debt leg's oracle price before it can be compared with an amount.
 *
 * Also corrected here: liquidation is **two-threshold** with a continuously
 * scaling penalty and close factor (soft → hard), so the single headline
 * `liquidationPenalty` is only the value at the SOFT threshold.
 */

/** WAD USD floor → raw base units of the debt asset at its oracle price. */
function minLoanInAssetUnits(
  minLoanUsdWad: unknown,
  priceUsdWad: unknown,
  decimals: unknown,
): string | undefined {
  const floor = toBig(minLoanUsdWad)
  const price = toBig(priceUsdWad)
  if (floor == null || price == null || floor === 0n || price === 0n) {
    return undefined
  }
  const dec = typeof decimals === 'number' ? decimals : Number(decimals)
  if (!Number.isFinite(dec) || dec < 0 || dec > 36) return undefined
  // floor[USD WAD] / price[USD WAD] * 10^dec — rounded UP, because this is a
  // gate: rounding down would produce an amount the chain still rejects.
  const scaled = floor * 10n ** BigInt(dec)
  const units = (scaled + price - 1n) / price
  return units > 0n ? units.toString() : undefined
}

function toBig(v: unknown): bigint | undefined {
  if (typeof v === 'bigint') return v
  if (typeof v === 'number' && Number.isFinite(v)) return BigInt(Math.trunc(v))
  if (typeof v === 'string' && /^\d+$/.test(v)) return BigInt(v)
  return undefined
}

export const curvanceAdapter: TermAdapter = {
  id: 'curvance',
  matches: isCurvance,
  profileId: () => 'curvance.dual@v1',
  build: (input) => {
    const cv = (input.market?.curvance ?? {}) as Record<string, unknown>

    const holdSecs =
      typeof cv.holdPeriodSeconds === 'number' && cv.holdPeriodSeconds > 0
        ? cv.holdPeriodSeconds
        : undefined

    // The debt leg's own price is what the protocol values debt with, so the
    // floor has to be converted with THAT and not with a market mid.
    const tokens = Array.isArray(cv.tokens)
      ? (cv.tokens as Record<string, unknown>[])
      : []
    const debtLeg =
      tokens.find(
        (t) =>
          String(t?.underlying ?? '').toLowerCase() ===
          String(input.underlying ?? '').toLowerCase(),
      ) ?? undefined

    const minDebt = minLoanInAssetUnits(
      cv.minLoanSizeUsdWad,
      debtLeg?.assetPrice,
      debtLeg?.assetDecimals ?? debtLeg?.decimals,
    )

    // Soft and hard thresholds, when the descriptor carries both — the single
    // headline penalty is the SOFT one and understates the terminal case.
    const softPen = numOrUndef(debtLeg?.liquidationPenaltySoft)
    const hardPen = numOrUndef(debtLeg?.liquidationPenaltyHard)

    return {
      constraints: {
        // One position per address per market; both legs share one health.
        positionModel: 'account',
        positionIdMeaning:
          'the borrower address itself — Curvance keys a position to the ' +
          'account, there is no loan id or NFT',
        crossMargin: false,
      },
      borrow: {
        debtShape: 'accruing',
        availability: {
          // A borrow below the floor REVERTS, so the CTA must be gated on it.
          minSize: minDebt,
          // The hold period blocks CLOSING, which the generic builder assumes
          // is always possible. Stated on the exit side below; `canClose`
          // stays true because it is per-ACCOUNT and time-bound, not a market
          // property — a market-level `false` here would read as "nobody can
          // ever exit", which is wrong and worse.
        },
        exit: {
          partialAllowed: true,
          minDebt,
          cooldownSecs: holdSecs,
        },
        ...(softPen != null || hardPen != null
          ? {
              liquidation: {
                // Two thresholds with a continuously scaling penalty and close
                // factor between them, plus a Fastlane/Atlas auction path that
                // can set both at runtime.
                model: 'dual-threshold',
                trigger: 'price',
                penalty: softPen ?? hardPen,
                penalties: [
                  ...(softPen != null
                    ? [
                        {
                          id: 'soft',
                          label: 'Soft threshold (partial)',
                          value: softPen,
                          description:
                            'First touch: a PARTIAL liquidation at the close ' +
                            'factor, at the lower penalty.',
                        },
                      ]
                    : []),
                  ...(hardPen != null
                    ? [
                        {
                          id: 'hard',
                          label: 'Hard threshold (full)',
                          value: hardPen,
                          description:
                            'Full liquidation at the higher penalty. The ' +
                            'penalty scales continuously between the two.',
                        },
                      ]
                    : []),
                ],
              },
            }
          : {}),
      },
      supply: {
        exit: {
          // The same 20 minutes blocks redemption and share transfers, not
          // just repayment.
          cooldownSecs: holdSecs,
        },
      },
    }
  },
}

function numOrUndef(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}
